import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Title,
  Group,
  Stack,
  Text,
  Button,
  Table,
  Checkbox,
  Badge,
  TextInput,
  Select,
  Loader,
  Pagination,
  SimpleGrid,
  Card,
  ThemeIcon,
  Tooltip,
  Modal,
  ActionIcon,
  Menu,
  Popover,
  Divider,
  Tabs,
  Alert,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconRefresh,
  IconSend,
  IconCheck,
  IconClock,
  IconAlertTriangle,
  IconSearch,
  IconBan,
  IconPlayerPlay,
  IconMail,
  IconPrinter,
  IconWorld,
  IconNetwork,
  IconFilter,
  IconBarcode,
  IconPlus,
  IconDotsVertical,
  IconArrowBackUp,
  IconHistory,
  IconPlayerPause,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  useDeliveryQueue,
  useDeliveryQueueStats,
  useBulkCancelDeliveries,
  useBulkRetryDeliveries,
  useProcessDeliveries,
  useProcessSelectedDeliveries,
  useBulkResendDeliveries,
  useSuppressedDeliveryMethods,
} from "../../hooks/useWorkflows";
import type {
  BulkActionResult,
  DeliveryQueueItem,
  DeliveryQueuePageRequest,
  DeliveryQueueStatus,
} from "../../services/workflows.service";
import { useBarcodeScanner } from "../../hooks/useBarcodeScanner";
import { CreateDeliveryModal } from "../../components/report-delivery/CreateDeliveryModal";
import { BulkDeliveryModal } from "../../components/report-delivery/BulkDeliveryModal";
import { SuppressedDeliveryMethodsAlert } from "../../components/report-delivery/SuppressedDeliveryMethodsAlert";
import { ManageSuppressedMethodsModal } from "../../components/report-delivery/ManageSuppressedMethodsModal";
import { HeldForReviewDeliveryBadge } from "../../components/report-delivery/HeldForReviewDeliveryBadge";
import { PortalDigestEmailTimingBadge } from "../../components/report-delivery/PortalDigestEmailTimingBadge";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  getPortalTokensByDelivery,
  revokePortalToken,
  restorePortalToken,
} from "../../services/signOff.service";
import {
  formatDeliveryMethod,
  formatDeliveryStatus,
  isReportOnPortal,
} from "../../utils/reportDeliveryLabels";

dayjs.extend(relativeTime);

const ITEMS_PER_PAGE = 20;

// Every status a delivery row can actually hold. 'deferred' remains for older
// rows and 'recalled' is used by the recall workflow. Statuses no code path
// persists are deliberately absent: an option that can never match reads as a
// broken filter.
const VALID_STATUSES: DeliveryQueueStatus[] = [
  "pending",
  "recalled",
  "deferred",
  "held",
  "sending",
  "delivered",
  "failed",
  "cancelled",
  "ready_for_print",
  "ready_for_pickup",
];
const VALID_METHODS = ["fax", "email", "hl7", "portal", "mail", "remote_print"];

const VALID_TABS = ["queue", "delivered", "cancelled"] as const;
type TabKey = (typeof VALID_TABS)[number];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "recalled", label: "Recalled" },
  { value: "deferred", label: "Deferred" },
  { value: "held", label: "Held for review" },
  { value: "sending", label: "Sending" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "ready_for_print", label: "Ready for Print" },
  { value: "ready_for_pickup", label: "Ready for Pickup" },
];

const METHOD_OPTIONS = [
  { value: "", label: "All Methods" },
  { value: "fax", label: "Fax" },
  { value: "email", label: "Email" },
  { value: "hl7", label: "HL7" },
  { value: "portal", label: "Portal" },
  { value: "mail", label: "Print/Mail" },
  { value: "remote_print", label: "Remote Print" },
];

function getRecipientTypeLabel(recipientType: string): string {
  switch (recipientType) {
    case "physician":
      return "Physician";
    case "practice":
      return "Practice";
    case "surgery_center":
      return "Surgical Center";
    case "lab":
      return "Lab";
    case "patient":
      return "Patient";
    default:
      return recipientType;
  }
}

function isDeliveryQueueStatus(value: string): value is DeliveryQueueStatus {
  return VALID_STATUSES.includes(value as DeliveryQueueStatus);
}

function getStatusColor(status: string): string {
  switch (status) {
    case "pending":
      return "blue";
    case "deferred":
      return "indigo";
    case "held":
      return "orange";
    case "sending":
      return "yellow";
    case "recalled":
      return "orange";
    case "delivered":
      return "green";
    case "ready_for_print":
    case "ready_for_pickup":
      return "cyan";
    case "printed":
      return "blue";
    case "mailed":
      return "teal";
    case "failed":
      return "red";
    case "cancelled":
      return "gray";
    default:
      return "gray";
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case "pending":
      return IconClock;
    case "deferred":
      return IconClock;
    case "held":
      return IconPlayerPause;
    case "sending":
      return IconSend;
    case "recalled":
      return IconArrowBackUp;
    case "delivered":
      return IconCheck;
    case "ready_for_print":
    case "ready_for_pickup":
    case "printed":
      return IconPrinter;
    case "mailed":
      return IconSend;
    case "failed":
      return IconAlertTriangle;
    case "cancelled":
      return IconBan;
    default:
      return IconClock;
  }
}

function isAutomaticallySuperseded(item: DeliveryQueueItem): boolean {
  return item.status === "cancelled" && item.isSuperseded === true;
}

function getStatusLabel(item: DeliveryQueueItem): string {
  if (isAutomaticallySuperseded(item)) return "Automatically superseded";
  return formatDeliveryStatus(item.status);
}

function canSelectForAdministrativeAction(item: DeliveryQueueItem): boolean {
  if (item.status === "recalled") return false;
  if (
    item.status === "pending" ||
    item.status === "sending" ||
    item.status === "deferred" ||
    item.status === "held"
  ) {
    return item.canCancel ?? true;
  }
  if (item.status === "failed" || item.status === "cancelled") {
    return item.canRetry ?? !isAutomaticallySuperseded(item);
  }
  return false;
}

function showBulkRetryResult(result: BulkActionResult): void {
  const retryErrors = result.errors.join("; ");
  notifications.show({
    title: "Retried",
    message: retryErrors
      ? `Successfully retried ${result.success} delivery(ies). ${retryErrors}`
      : `Successfully retried ${result.success} delivery(ies)`,
    color: result.failed > 0 ? "yellow" : "green",
  });
}

function showBulkRetryFailure(): void {
  notifications.show({
    title: "Error",
    message: "Failed to retry deliveries. Please try again.",
    color: "red",
  });
}

function getRecallConsequenceLabel(
  outcome: DeliveryQueueItem["recallOutcome"]
): string {
  const labels: Record<
    NonNullable<DeliveryQueueItem["recallOutcome"]>,
    string
  > = {
    recalled: "Stopped before dispatch",
    already_recalled: "Already recalled",
    dispatch_started: "Dispatch had already started",
    already_sent: "Already sent or delivered",
    failed: "Delivery had already failed",
    cancelled: "Already administratively cancelled",
    superseded: "Automatically superseded",
    legacy_unbound: "Report version unavailable",
    unavailable: "Could not be stopped",
  };
  return outcome ? labels[outcome] : "Outcome unavailable";
}

function getRecallAuthorityLabel(authorityBasis: string): string {
  if (authorityBasis === "assigned_pathologist") return "Assigned pathologist";
  if (authorityBasis === "any_case_capability")
    return "Clinical/administrative override";
  return "Denied";
}

function formatRecallAttemptOutcome(outcome: string): string {
  return outcome
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function recallOutcomeNeedsFollowUp(
  outcome: DeliveryQueueItem["recallOutcome"]
): boolean {
  return outcome === "dispatch_started" || outcome === "already_sent";
}

function getMethodIcon(method: string) {
  const m = method?.toLowerCase() || "";
  if (m.includes("email")) return IconMail;
  if (m.includes("fax")) return IconPrinter;
  if (m.includes("hl7")) return IconNetwork;
  if (m.includes("portal")) return IconWorld;
  return IconSend;
}

function getTimeUntil(scheduledAt: string): string {
  const scheduled = dayjs(scheduledAt);
  const now = dayjs();
  if (scheduled.isBefore(now)) {
    return "Now";
  }
  const diffMs = scheduled.diff(now);
  const diffMinutes = Math.ceil(diffMs / 60000);
  if (diffMinutes <= 0) return "Now";
  if (diffMinutes === 1) return "1 min";
  return `${diffMinutes} min`;
}

export default function ReportDeliveryQueuePage() {
  const [selectedItems, setSelectedItems] = useState<
    Map<string, DeliveryQueueItem>
  >(() => new Map());
  const [selectedDeliveredItems, setSelectedDeliveredItems] = useState<
    Map<string, DeliveryQueueItem>
  >(() => new Map());
  const [searchParams, setSearchParams] = useSearchParams();

  const searchValue = searchParams.get("search") ?? "";
  const requestedStatus = searchParams.get("status") ?? "";
  const statusFilter = isDeliveryQueueStatus(requestedStatus)
    ? requestedStatus
    : "";
  const methodFilter = VALID_METHODS.includes(searchParams.get("method") ?? "")
    ? searchParams.get("method") ?? ""
    : "";
  const showFailedOnly = searchParams.get("failed") === "1";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const deliveredPage = Math.max(
    1,
    parseInt(searchParams.get("dpage") ?? "1", 10) || 1
  );
  const cancelledPage = Math.max(
    1,
    parseInt(searchParams.get("cpage") ?? "1", 10) || 1
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === "") next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const activeTab: TabKey = VALID_TABS.includes(
    searchParams.get("tab") as TabKey
  )
    ? (searchParams.get("tab") as TabKey)
    : "queue";

  const handleTabChange = useCallback(
    (value: string | null) => {
      if (!value || !VALID_TABS.includes(value as TabKey)) return;
      updateParams({
        tab: value === "queue" ? null : value,
        page: null,
        dpage: null,
        cpage: null,
      });
      setSelectedItems(new Map());
      setSelectedDeliveredItems(new Map());
    },
    [updateParams]
  );

  const [scannerInput, setScannerInput] = useState("");
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    createDeliveryOpened,
    { open: openCreateDelivery, close: closeCreateDelivery },
  ] = useDisclosure(false);
  const [
    bulkDeliveryOpened,
    { open: openBulkDelivery, close: closeBulkDelivery },
  ] = useDisclosure(false);
  const [
    manageHeldMethodsOpened,
    { open: openManageHeldMethods, close: closeManageHeldMethods },
  ] = useDisclosure(false);
  const canManageHeldMethods = useHasPermission(
    "report_delivery.suppression_manage"
  );

  // Temp filters for modal
  const [tempSearchValue, setTempSearchValue] = useState("");
  const [tempStatusFilter, setTempStatusFilter] = useState("");
  const [tempMethodFilter, setTempMethodFilter] = useState("");
  const [tempShowFailedOnly, setTempShowFailedOnly] = useState(false);
  const hasActiveFilters = useMemo(
    () => !!(searchValue || statusFilter || methodFilter || showFailedOnly),
    [methodFilter, searchValue, showFailedOnly, statusFilter]
  );

  const deliveryQueueRequest: DeliveryQueuePageRequest = useMemo(
    () => ({
      view: activeTab === "queue" ? "active" : activeTab,
      page:
        activeTab === "queue"
          ? page
          : activeTab === "delivered"
          ? deliveredPage
          : cancelledPage,
      pageSize: ITEMS_PER_PAGE,
      search: searchValue || undefined,
      status: statusFilter ? [statusFilter] : undefined,
      deliveryMethod: methodFilter ? [methodFilter] : undefined,
      failedOnly: showFailedOnly || undefined,
    }),
    [
      activeTab,
      cancelledPage,
      deliveredPage,
      methodFilter,
      page,
      searchValue,
      showFailedOnly,
      statusFilter,
    ]
  );

  const {
    data: queuePage,
    isLoading,
    isFetching,
    isError,
    isPlaceholderData,
    refetch,
  } = useDeliveryQueue(deliveryQueueRequest);
  const { data: stats } = useDeliveryQueueStats();
  const { data: suppressedMethods } = useSuppressedDeliveryMethods();
  const cancelMutation = useBulkCancelDeliveries();
  const retryMutation = useBulkRetryDeliveries();
  const processMutation = useProcessDeliveries();
  const processSelectedMutation = useProcessSelectedDeliveries();
  const resendMutation = useBulkResendDeliveries();
  const blockedMethods = useMemo(
    () => suppressedMethods?.methods ?? [],
    [suppressedMethods]
  );
  const availableMethods = useMemo(
    () => suppressedMethods?.available ?? [],
    [suppressedMethods]
  );

  const queueItems = useMemo(() => queuePage?.items ?? [], [queuePage]);
  const queueViewItems = useMemo(
    () => (activeTab !== "delivered" && !isPlaceholderData ? queueItems : []),
    [activeTab, isPlaceholderData, queueItems]
  );
  const deliveredItems = useMemo(
    () => (activeTab === "delivered" && !isPlaceholderData ? queueItems : []),
    [activeTab, isPlaceholderData, queueItems]
  );
  const totalPages = useMemo(
    () =>
      activeTab !== "delivered" ? queuePage?.pagination.totalPages ?? 0 : 0,
    [activeTab, queuePage]
  );
  const deliveredTotalPages = useMemo(
    () =>
      activeTab === "delivered" ? queuePage?.pagination.totalPages ?? 0 : 0,
    [activeTab, queuePage]
  );
  const activeTotal = useMemo(
    () => queuePage?.matching.active ?? 0,
    [queuePage]
  );
  const deliveredTotal = useMemo(
    () => queuePage?.matching.delivered ?? 0,
    [queuePage]
  );
  const cancelledTotal = useMemo(
    () => queuePage?.matching.cancelled ?? 0,
    [queuePage]
  );
  const queueViewTotal = useMemo(
    () => (activeTab === "cancelled" ? cancelledTotal : activeTotal),
    [activeTab, activeTotal, cancelledTotal]
  );
  const matchingTotal = useMemo(
    () => queuePage?.matching.total ?? 0,
    [queuePage]
  );
  const matchingDescription = useMemo(() => {
    if (!hasActiveFilters) {
      return `${matchingTotal} ${
        matchingTotal === 1 ? "delivery" : "deliveries"
      }`;
    }
    return `${matchingTotal} ${
      matchingTotal === 1 ? "delivery matches" : "deliveries match"
    } the current filters`;
  }, [hasActiveFilters, matchingTotal]);
  const activePageNumber = useMemo(
    () =>
      activeTab === "cancelled"
        ? queuePage?.pagination.page ?? cancelledPage
        : queuePage?.pagination.page ?? page,
    [activeTab, cancelledPage, page, queuePage]
  );
  const deliveredPageNumber = useMemo(
    () =>
      activeTab === "delivered"
        ? queuePage?.pagination.page ?? deliveredPage
        : deliveredPage,
    [activeTab, deliveredPage, queuePage]
  );
  const selectablePaginatedItems = useMemo(
    () => queueViewItems.filter(canSelectForAdministrativeAction),
    [queueViewItems]
  );

  // Selection handlers
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedItems((currentItems) => {
        const nextItems = new Map(currentItems);
        selectablePaginatedItems.forEach((item) => {
          if (checked) nextItems.set(item.id, item);
          else nextItems.delete(item.id);
        });
        return nextItems;
      });
    },
    [selectablePaginatedItems]
  );

  const handleSelectAllChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSelectAll(event.currentTarget.checked);
    },
    [handleSelectAll]
  );

  const handleSelectItemChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const deliveryId = event.currentTarget.dataset.deliveryId;
      if (!deliveryId) return;
      const item = queueViewItems.find(
        (delivery) => delivery.id === deliveryId
      );
      if (!item) return;
      const checked = event.currentTarget.checked;
      setSelectedItems((currentItems) => {
        const nextItems = new Map(currentItems);
        if (checked) nextItems.set(deliveryId, item);
        else nextItems.delete(deliveryId);
        return nextItems;
      });
    },
    [queueViewItems]
  );

  // Bulk actions
  const handleBulkCancel = useCallback(async () => {
    const ids = Array.from(selectedItems.values())
      .filter(
        (item) =>
          ["pending", "sending", "deferred", "held"].includes(item.status) &&
          (item.canCancel ?? true)
      )
      .map((item) => item.id);
    if (ids.length === 0) return;

    try {
      const result = await cancelMutation.mutateAsync(ids);
      notifications.show({
        title: "Cancelled",
        message: `Successfully cancelled ${result.success} delivery(ies)${
          result.failed > 0 ? `, ${result.failed} failed` : ""
        }`,
        color: result.failed > 0 ? "yellow" : "green",
      });
      setSelectedItems(new Map());
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to cancel deliveries. Please try again.",
        color: "red",
      });
    }
  }, [cancelMutation, selectedItems]);

  const handleBulkRetry = useCallback(async () => {
    const ids = Array.from(selectedItems.values())
      .filter(
        (item) =>
          (item.status === "failed" || item.status === "cancelled") &&
          (item.canRetry ?? !isAutomaticallySuperseded(item))
      )
      .map((item) => item.id);
    if (ids.length === 0) return;

    try {
      const result = await retryMutation.mutateAsync(ids);
      showBulkRetryResult(result);
      setSelectedItems(new Map());
    } catch {
      showBulkRetryFailure();
    }
  }, [retryMutation, selectedItems]);

  // Count selected items by state
  const selectedPending = useMemo(
    () =>
      Array.from(selectedItems.values()).filter(
        (item) => item.status === "pending"
      ).length,
    [selectedItems]
  );

  const selectedForCancel = useMemo(
    () =>
      Array.from(selectedItems.values()).filter(
        (item) =>
          ["pending", "sending", "deferred", "held"].includes(item.status) &&
          (item.canCancel ?? true)
      ).length,
    [selectedItems]
  );

  const selectedForRetry = useMemo(
    () =>
      Array.from(selectedItems.values()).filter(
        (item) =>
          (item.status === "failed" || item.status === "cancelled") &&
          (item.canRetry ?? !isAutomaticallySuperseded(item))
      ).length,
    [selectedItems]
  );

  const allSelected = useMemo(
    () =>
      selectablePaginatedItems.length > 0 &&
      selectablePaginatedItems.every((item) => selectedItems.has(item.id)),
    [selectablePaginatedItems, selectedItems]
  );
  const currentPageSelectedCount = useMemo(
    () =>
      selectablePaginatedItems.filter((item) => selectedItems.has(item.id))
        .length,
    [selectablePaginatedItems, selectedItems]
  );

  // Portal revocation state
  const [portalActionItemId, setPortalActionItemId] = useState<string | null>(
    null
  );
  const [portalConfirm, setPortalConfirm] = useState<{
    deliveryId: string;
    action: "revoke" | "restore";
  } | null>(null);
  // Track which deliveries have revoked tokens: deliveryId -> boolean
  const [revokedPortalMap, setRevokedPortalMap] = useState<
    Record<string, boolean>
  >({});
  const checkedPortalDeliveryIds = useRef(new Set<string>());

  const checkPortalTokenStatus = useCallback(
    async (deliveryQueueId: string) => {
      try {
        const tokens = await getPortalTokensByDelivery(deliveryQueueId);
        const allRevoked =
          tokens.length > 0 && tokens.every((t) => !!t.revokedAt);
        setRevokedPortalMap((prev) => ({
          ...prev,
          [deliveryQueueId]: allRevoked,
        }));
      } catch {
        // ignore
      }
    },
    []
  );

  // Load portal token status for delivered portal items
  useEffect(() => {
    if (!deliveredItems) return;
    const portalDeliveries = deliveredItems.filter((item) =>
      item.deliveryMethod?.toLowerCase().includes("portal")
    );
    portalDeliveries.forEach((item) => {
      if (checkedPortalDeliveryIds.current.has(item.id)) return;
      checkedPortalDeliveryIds.current.add(item.id);
      checkPortalTokenStatus(item.id);
    });
  }, [deliveredItems, checkPortalTokenStatus]);

  const handleRevokePortalAccess = useCallback(
    async (deliveryQueueId: string) => {
      setPortalConfirm(null);
      setPortalActionItemId(deliveryQueueId);
      try {
        const tokens = await getPortalTokensByDelivery(deliveryQueueId);
        const activeTokens = tokens.filter((token) => !token.revokedAt);
        if (activeTokens.length === 0) {
          notifications.show({
            title: "No Active Tokens",
            message: "No active portal tokens found for this delivery",
            color: "yellow",
          });
          return;
        }
        await Promise.all(
          activeTokens.map((token) => revokePortalToken(token.id))
        );
        notifications.show({
          title: "Revoked",
          message: `Successfully revoked ${activeTokens.length} portal token(s)`,
          color: "green",
        });
        setRevokedPortalMap((previous) => ({
          ...previous,
          [deliveryQueueId]: true,
        }));
        refetch();
      } catch {
        notifications.show({
          title: "Error",
          message: "Failed to revoke portal access. Please try again.",
          color: "red",
        });
      } finally {
        setPortalActionItemId(null);
      }
    },
    [refetch]
  );

  const handleRestorePortalAccess = useCallback(
    async (deliveryQueueId: string) => {
      setPortalConfirm(null);
      setPortalActionItemId(deliveryQueueId);
      try {
        const tokens = await getPortalTokensByDelivery(deliveryQueueId);
        const revokedTokens = tokens.filter((token) => !!token.revokedAt);
        if (revokedTokens.length === 0) {
          notifications.show({
            title: "No Revoked Tokens",
            message: "No revoked portal tokens found for this delivery",
            color: "yellow",
          });
          return;
        }
        await Promise.all(
          revokedTokens.map((token) => restorePortalToken(token.id))
        );
        notifications.show({
          title: "Access Restored",
          message: `Successfully restored portal access for ${revokedTokens.length} token(s)`,
          color: "green",
        });
        setRevokedPortalMap((previous) => ({
          ...previous,
          [deliveryQueueId]: false,
        }));
        refetch();
      } catch {
        notifications.show({
          title: "Error",
          message: "Failed to restore portal access. Please try again.",
          color: "red",
        });
      } finally {
        setPortalActionItemId(null);
      }
    },
    [refetch]
  );

  const handleSelectAllDelivered = useCallback(
    (checked: boolean) => {
      setSelectedDeliveredItems((currentItems) => {
        const nextItems = new Map(currentItems);
        deliveredItems.forEach((item) => {
          if (checked) nextItems.set(item.id, item);
          else nextItems.delete(item.id);
        });
        return nextItems;
      });
    },
    [deliveredItems]
  );

  const handleSelectAllDeliveredChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSelectAllDelivered(event.currentTarget.checked);
    },
    [handleSelectAllDelivered]
  );

  const handleSelectDeliveredItemChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const deliveryId = event.currentTarget.dataset.deliveryId;
      if (!deliveryId) return;
      const checked = event.currentTarget.checked;
      const item = deliveredItems.find(
        (delivery) => delivery.id === deliveryId
      );
      if (!item) return;
      setSelectedDeliveredItems((currentItems) => {
        const nextItems = new Map(currentItems);
        if (checked) nextItems.set(deliveryId, item);
        else nextItems.delete(deliveryId);
        return nextItems;
      });
    },
    [deliveredItems]
  );

  const handleRestorePortalRequest = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const deliveryId = event.currentTarget.dataset.deliveryId;
      if (deliveryId) setPortalConfirm({ deliveryId, action: "restore" });
    },
    []
  );

  const handleRevokePortalRequest = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const deliveryId = event.currentTarget.dataset.deliveryId;
      if (deliveryId) setPortalConfirm({ deliveryId, action: "revoke" });
    },
    []
  );

  const allDeliveredSelected = useMemo(
    () =>
      deliveredItems.length > 0 &&
      deliveredItems.every((item) => selectedDeliveredItems.has(item.id)),
    [deliveredItems, selectedDeliveredItems]
  );
  const currentDeliveredPageSelectedCount = useMemo(
    () =>
      deliveredItems.filter((item) => selectedDeliveredItems.has(item.id))
        .length,
    [deliveredItems, selectedDeliveredItems]
  );

  const handleBulkResend = useCallback(async () => {
    const ids = Array.from(selectedDeliveredItems.keys());
    if (ids.length === 0) return;

    try {
      const result = await resendMutation.mutateAsync(ids);
      notifications.show({
        title: "Resent",
        message: `Successfully queued ${
          result.success
        } delivery(ies) for resend${
          result.failed > 0 ? `, ${result.failed} failed` : ""
        }`,
        color: result.failed > 0 ? "yellow" : "green",
      });
      setSelectedDeliveredItems(new Map());
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to resend deliveries. Please try again.",
        color: "red",
      });
    }
  }, [resendMutation, selectedDeliveredItems]);

  // Process pending deliveries
  const handleProcessNow = useCallback(async () => {
    try {
      const result = await processMutation.mutateAsync();
      notifications.show({
        title: "Processing",
        message: result.message,
        color: result.queued > 0 ? "green" : "blue",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to process deliveries. Please try again.",
        color: "red",
      });
    }
  }, [processMutation]);

  const handleProcessSelected = useCallback(async () => {
    const ids = Array.from(selectedItems.values())
      .filter((item) => item.status === "pending")
      .map((item) => item.id);

    if (ids.length === 0) return;

    try {
      const result = await processSelectedMutation.mutateAsync(ids);
      notifications.show({
        title: "Processing Selected",
        message: result.message,
        color: result.queued > 0 ? "green" : "blue",
      });
      setSelectedItems(new Map());
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to process selected deliveries. Please try again.",
        color: "red",
      });
    }
  }, [processSelectedMutation, selectedItems]);

  const handleScannerSubmit = useCallback(() => {
    if (!scannerInput.trim()) return;
    const caseNumber = scannerInput.trim().split(";")[0].trim();
    if (!caseNumber) return;
    updateParams({ search: caseNumber, page: null, dpage: null, cpage: null });
    setScannerInput("");
    setSelectedItems(new Map());
    setSelectedDeliveredItems(new Map());
  }, [scannerInput, updateParams]);

  const handleScannerKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== "Enter") return;
      handleScannerSubmit();
    },
    [handleScannerSubmit]
  );

  useBarcodeScanner(
    useCallback(
      (barcode: string) => {
        const caseNumber = barcode.split(";")[0].trim();
        if (!caseNumber) return;
        updateParams({
          search: caseNumber,
          page: null,
          dpage: null,
          cpage: null,
        });
        setSelectedItems(new Map());
        setSelectedDeliveredItems(new Map());
      },
      [updateParams]
    )
  );

  const handleClearFilters = useCallback(() => {
    updateParams({
      search: null,
      status: null,
      method: null,
      failed: null,
      page: null,
      dpage: null,
      cpage: null,
    });
    setSelectedItems(new Map());
    setSelectedDeliveredItems(new Map());
  }, [updateParams]);

  // Open filter modal - sync temp values
  const handleOpenFilterModal = useCallback(() => {
    setTempSearchValue(searchValue);
    setTempStatusFilter(statusFilter);
    setTempMethodFilter(methodFilter);
    setTempShowFailedOnly(showFailedOnly);
    openFilterModal();
  }, [
    methodFilter,
    openFilterModal,
    searchValue,
    showFailedOnly,
    statusFilter,
  ]);

  // Apply filters from modal
  const handleApplyFilters = useCallback(() => {
    updateParams({
      search: tempSearchValue || null,
      status: tempStatusFilter || null,
      method: tempMethodFilter || null,
      failed: tempShowFailedOnly ? "1" : null,
      page: null,
      dpage: null,
      cpage: null,
    });
    setSelectedItems(new Map());
    setSelectedDeliveredItems(new Map());
    closeFilterModal();
  }, [
    tempSearchValue,
    tempStatusFilter,
    tempMethodFilter,
    tempShowFailedOnly,
    updateParams,
    closeFilterModal,
  ]);

  // Clear temp filters in modal
  const handleClearTempFilters = useCallback(() => {
    setTempSearchValue("");
    setTempStatusFilter("");
    setTempMethodFilter("");
    setTempShowFailedOnly(false);
  }, []);

  const handleScannerInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setScannerInput(event.currentTarget.value);
    },
    []
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleActivePageChange = useCallback(
    (nextPage: number) => {
      updateParams(
        activeTab === "cancelled"
          ? { cpage: String(nextPage) }
          : { page: String(nextPage) }
      );
    },
    [activeTab, updateParams]
  );

  const handleDeliveredPageChange = useCallback(
    (nextPage: number) => {
      updateParams({ dpage: String(nextPage) });
    },
    [updateParams]
  );

  const handleTempSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTempSearchValue(event.currentTarget.value);
    },
    []
  );

  const handleTempStatusChange = useCallback((value: string | null) => {
    setTempStatusFilter(value || "");
  }, []);

  const handleTempMethodChange = useCallback((value: string | null) => {
    setTempMethodFilter(value || "");
  }, []);

  const handleTempFailedOnlyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTempShowFailedOnly(event.currentTarget.checked);
    },
    []
  );

  const handleClosePortalConfirmation = useCallback(() => {
    setPortalConfirm(null);
  }, []);

  const handleConfirmPortalAction = useCallback(() => {
    if (!portalConfirm) return;
    if (portalConfirm.action === "revoke") {
      handleRevokePortalAccess(portalConfirm.deliveryId);
      return;
    }
    handleRestorePortalAccess(portalConfirm.deliveryId);
  }, [handleRestorePortalAccess, handleRevokePortalAccess, portalConfirm]);

  const handleCreateDeliverySuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="center">
            <Title order={2}>Report Delivery Queue</Title>
            <Group gap="sm">
              {canManageHeldMethods && (
                <Button
                  variant="light"
                  color="orange"
                  leftSection={<IconPlayerPause size={16} />}
                  onClick={openManageHeldMethods}
                >
                  Manage Held Methods
                  {blockedMethods.length > 0 && ` (${blockedMethods.length})`}
                </Button>
              )}
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={openCreateDelivery}
              >
                Create New Delivery
              </Button>
              <Button
                variant="light"
                leftSection={<IconSend size={16} />}
                onClick={openBulkDelivery}
              >
                New Bulk Delivery
              </Button>
            </Group>
          </Group>

          {/* Stats Cards - At the top */}
          {stats && (
            <SimpleGrid cols={{ base: 2, sm: 4, lg: 8 }} spacing="md">
              <Card withBorder p="sm">
                <Group gap="xs">
                  <ThemeIcon color="blue" variant="light" size="lg">
                    <IconClock size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">
                      Pending
                    </Text>
                    <Text fw={700} size="lg">
                      {stats.pending}
                    </Text>
                  </div>
                </Group>
              </Card>
              <Card withBorder p="sm">
                <Group gap="xs">
                  <ThemeIcon color="orange" variant="light" size="lg">
                    <IconArrowBackUp size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">
                      Recalled
                    </Text>
                    <Text fw={700} size="lg">
                      {stats.recalled}
                    </Text>
                  </div>
                </Group>
              </Card>
              <Card withBorder p="sm">
                <Group gap="xs">
                  <ThemeIcon color="orange" variant="light" size="lg">
                    <IconPlayerPause size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">
                      Held for review
                    </Text>
                    <Text fw={700} size="lg">
                      {stats.held}
                    </Text>
                  </div>
                </Group>
              </Card>
              <Card withBorder p="sm">
                <Group gap="xs">
                  <ThemeIcon color="yellow" variant="light" size="lg">
                    <IconSend size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">
                      Sending
                    </Text>
                    <Text fw={700} size="lg">
                      {stats.sending}
                    </Text>
                  </div>
                </Group>
              </Card>
              <Card withBorder p="sm">
                <Group gap="xs">
                  <ThemeIcon color="green" variant="light" size="lg">
                    <IconCheck size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">
                      Delivered
                    </Text>
                    <Text fw={700} size="lg">
                      {stats.delivered}
                    </Text>
                  </div>
                </Group>
              </Card>
              <Card withBorder p="sm">
                <Group gap="xs">
                  <ThemeIcon color="red" variant="light" size="lg">
                    <IconAlertTriangle size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">
                      Failed
                    </Text>
                    <Text fw={700} size="lg">
                      {stats.failed}
                    </Text>
                  </div>
                </Group>
              </Card>
              <Card withBorder p="sm">
                <Group gap="xs">
                  <ThemeIcon color="gray" variant="light" size="lg">
                    <IconBan size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">
                      Cancelled
                    </Text>
                    <Text fw={700} size="lg">
                      {stats.cancelled}
                    </Text>
                  </div>
                </Group>
              </Card>
            </SimpleGrid>
          )}

          {/* Scanner and Filters Row */}
          <Group align="stretch" grow>
            {/* Scanner Panel */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconBarcode size={24} />
                  <Text fw={500}>Scan Case Barcode</Text>
                </Group>
                <Group>
                  <TextInput
                    placeholder="Scan or enter case number (e.g., G25-00001)"
                    value={scannerInput}
                    onChange={handleScannerInputChange}
                    onKeyDown={handleScannerKeyPress}
                    style={{ flex: 1 }}
                    autoFocus
                  />
                  <Button
                    onClick={handleScannerSubmit}
                    disabled={!scannerInput.trim()}
                  >
                    Find
                  </Button>
                </Group>
                <Group>
                  <Button
                    leftSection={<IconSend size={16} />}
                    variant="filled"
                    color="green"
                    onClick={handleProcessNow}
                    loading={processMutation.isPending}
                    size="sm"
                  >
                    Process all now
                  </Button>
                  <Button
                    leftSection={<IconBan size={16} />}
                    variant="light"
                    color="red"
                    disabled={selectedForCancel === 0 || isPlaceholderData}
                    loading={cancelMutation.isPending}
                    onClick={handleBulkCancel}
                    size="sm"
                  >
                    Cancel ({selectedForCancel})
                  </Button>
                  <Button
                    leftSection={<IconPlayerPlay size={16} />}
                    variant="light"
                    color="blue"
                    disabled={selectedForRetry === 0 || isPlaceholderData}
                    loading={retryMutation.isPending}
                    onClick={handleBulkRetry}
                    size="sm"
                  >
                    Retry ({selectedForRetry})
                  </Button>
                </Group>
                <Text size="sm" c="dimmed">
                  Scan a case barcode to find and select its delivery items
                </Text>
              </Stack>
            </Paper>

            {/* Queue Filters */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={24} />
                    <Text fw={500}>Queue Filters</Text>
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    onClick={handleRefresh}
                    loading={isFetching}
                    aria-label="Refresh delivery queue"
                  >
                    <IconRefresh size={18} />
                  </ActionIcon>
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    {searchValue && (
                      <Badge variant="light" size="sm">
                        Search: {searchValue}
                      </Badge>
                    )}
                    {statusFilter && (
                      <Badge variant="light" size="sm">
                        Status: {statusFilter}
                      </Badge>
                    )}
                    {methodFilter && (
                      <Badge variant="light" size="sm">
                        Method: {methodFilter}
                      </Badge>
                    )}
                    {showFailedOnly && (
                      <Badge variant="light" color="red" size="sm">
                        Failed Only
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {matchingDescription}
                </Text>
              </Stack>
            </Paper>
          </Group>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab value="queue">Queue ({activeTotal})</Tabs.Tab>
              <Tabs.Tab value="delivered">
                Delivered ({deliveredTotal})
              </Tabs.Tab>
              <Tabs.Tab value="cancelled">
                Cancelled ({cancelledTotal})
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel
              value={activeTab === "cancelled" ? "cancelled" : "queue"}
              pt="md"
            >
              <Paper p="md" withBorder>
                <Stack gap="md">
                  {activeTab === "queue" && (
                    <SuppressedDeliveryMethodsAlert methods={blockedMethods} />
                  )}
                  <Group justify="space-between">
                    <Group gap="sm">
                      <Text fw={500} size="lg">
                        {activeTab === "cancelled"
                          ? `Cancelled History (${cancelledTotal})`
                          : `Active Queue (${activeTotal})`}
                      </Text>
                      {activeTab === "queue" && (
                        <Button
                          leftSection={<IconSend size={16} />}
                          variant="light"
                          color="green"
                          size="xs"
                          disabled={selectedPending === 0 || isPlaceholderData}
                          loading={processSelectedMutation.isPending}
                          onClick={handleProcessSelected}
                        >
                          Process now ({selectedPending})
                        </Button>
                      )}
                    </Group>
                    {totalPages > 1 && (
                      <Group gap="md">
                        <Text size="sm" c="dimmed">
                          Showing {(activePageNumber - 1) * ITEMS_PER_PAGE + 1}-
                          {Math.min(
                            activePageNumber * ITEMS_PER_PAGE,
                            queueViewTotal
                          )}{" "}
                          of {queueViewTotal}
                        </Text>
                        <Pagination
                          total={totalPages}
                          value={activePageNumber}
                          onChange={handleActivePageChange}
                          size="sm"
                        />
                      </Group>
                    )}
                  </Group>

                  {isError ? (
                    <Alert
                      color="red"
                      variant="light"
                      title={
                        activeTab === "cancelled"
                          ? "Could not load cancelled delivery history"
                          : "Could not load the active delivery queue"
                      }
                      role="alert"
                    >
                      Please refresh the page and try again.
                    </Alert>
                  ) : isLoading || isPlaceholderData ? (
                    <Group justify="center" p="xl">
                      <Loader />
                    </Group>
                  ) : queueViewItems.length === 0 ? (
                    <Text c="dimmed" ta="center" py="xl">
                      {activeTab === "cancelled"
                        ? hasActiveFilters
                          ? "No cancelled deliveries match the current filters"
                          : "No cancelled delivery history"
                        : hasActiveFilters
                        ? "No active deliveries match the current filters"
                        : "No active deliveries in queue"}
                    </Text>
                  ) : (
                    <Table.ScrollContainer minWidth={1100} type="native">
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th w={40}>
                              <Checkbox
                                checked={allSelected}
                                indeterminate={
                                  currentPageSelectedCount > 0 && !allSelected
                                }
                                onChange={handleSelectAllChange}
                                disabled={selectablePaginatedItems.length === 0}
                                aria-label="Select actionable deliveries"
                              />
                            </Table.Th>
                            <Table.Th>Case</Table.Th>
                            <Table.Th>Recipient</Table.Th>
                            <Table.Th>Method</Table.Th>
                            <Table.Th>Destination</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Scheduled</Table.Th>
                            <Table.Th>Sent At</Table.Th>
                            <Table.Th>Attempts</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {queueViewItems.map((item) => {
                            const StatusIcon = getStatusIcon(item.status);
                            const MethodIcon = getMethodIcon(
                              item.deliveryMethod
                            );
                            return (
                              <Table.Tr
                                key={item.id}
                                bg={
                                  item.deliverySuppressed
                                    ? "var(--mantine-color-orange-light)"
                                    : undefined
                                }
                              >
                                <Table.Td>
                                  <Checkbox
                                    checked={selectedItems.has(item.id)}
                                    onChange={handleSelectItemChange}
                                    data-delivery-id={item.id}
                                    disabled={
                                      !canSelectForAdministrativeAction(item)
                                    }
                                    aria-label={`Select delivery for ${item.caseNumber}`}
                                  />
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" fw={500}>
                                    {item.caseNumber}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Stack gap={0}>
                                    <Text size="sm">{item.recipientName}</Text>
                                    <Text size="xs" c="dimmed">
                                      {getRecipientTypeLabel(
                                        item.recipientType
                                      )}
                                    </Text>
                                  </Stack>
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs">
                                    <ThemeIcon
                                      color={
                                        item.deliverySuppressed
                                          ? "orange"
                                          : "gray"
                                      }
                                      variant="light"
                                      size="sm"
                                    >
                                      <MethodIcon size={14} />
                                    </ThemeIcon>
                                    <Text size="sm">
                                      {formatDeliveryMethod(
                                        item.deliveryMethod
                                      )}
                                    </Text>
                                    {item.deliverySuppressed && (
                                      <Tooltip
                                        label="Delivery is paused for this method. This report stays pending until it is released."
                                        maw={300}
                                        multiline
                                        withArrow
                                      >
                                        <Badge
                                          color="orange"
                                          variant="filled"
                                          size="xs"
                                          leftSection={
                                            <IconPlayerPause size={10} />
                                          }
                                        >
                                          Held
                                        </Badge>
                                      </Tooltip>
                                    )}
                                  </Group>
                                </Table.Td>
                                <Table.Td>
                                  <Text
                                    size="sm"
                                    style={{ maxWidth: 150 }}
                                    truncate
                                  >
                                    {item.destination}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Stack gap={4} align="flex-start">
                                    <Badge
                                      color={getStatusColor(item.status)}
                                      variant="light"
                                      leftSection={<StatusIcon size={12} />}
                                    >
                                      {getStatusLabel(item)}
                                    </Badge>
                                    {item.digestState === "pending" && (
                                      <PortalDigestEmailTimingBadge
                                        reportAvailable={isReportOnPortal(
                                          item.status
                                        )}
                                      />
                                    )}
                                    {item.heldEmailId && (
                                      <HeldForReviewDeliveryBadge
                                        heldEmailId={item.heldEmailId}
                                      />
                                    )}
                                    {recallOutcomeNeedsFollowUp(
                                      item.recallOutcome
                                    ) && (
                                      <Badge
                                        color="red"
                                        variant="filled"
                                        size="sm"
                                      >
                                        Could not be stopped — follow up
                                        required
                                      </Badge>
                                    )}
                                    {!!item.recallHistory?.length && (
                                      <Popover
                                        width="min(360px, calc(100vw - 32px))"
                                        position="bottom-start"
                                        offset={8}
                                        withinPortal
                                        shadow="md"
                                        withArrow
                                      >
                                        <Popover.Target>
                                          <Button
                                            variant="subtle"
                                            size="compact-xs"
                                            leftSection={
                                              <IconHistory
                                                size={13}
                                                aria-hidden="true"
                                              />
                                            }
                                            aria-label={`View recall details for ${item.caseNumber}, ${item.destination}`}
                                          >
                                            Recall details
                                          </Button>
                                        </Popover.Target>
                                        <Popover.Dropdown>
                                          <Stack gap="sm">
                                            <Text fw={600} size="sm">
                                              Recall details
                                            </Text>
                                            {(item.recallHistory ?? []).map(
                                              (attempt, index) => (
                                                <Stack
                                                  gap={4}
                                                  key={attempt.attemptId}
                                                >
                                                  {index > 0 && (
                                                    <Divider my={4} />
                                                  )}
                                                  <Text size="xs" fw={600}>
                                                    {getRecallConsequenceLabel(
                                                      attempt.deliveryOutcome
                                                        .result
                                                    )}
                                                  </Text>
                                                  <Text size="xs">
                                                    Destination:{" "}
                                                    {attempt.deliveryOutcome
                                                      .destination ||
                                                      item.destination ||
                                                      "Not available"}
                                                  </Text>
                                                  <Text size="xs">
                                                    Method:{" "}
                                                    {attempt.deliveryOutcome
                                                      .deliveryMethodName ||
                                                      item.deliveryMethod ||
                                                      "Not available"}
                                                  </Text>
                                                  <Text size="xs">
                                                    State:{" "}
                                                    {formatDeliveryStatus(
                                                      attempt.deliveryOutcome
                                                        .statusBefore
                                                    )}{" "}
                                                    →{" "}
                                                    {formatDeliveryStatus(
                                                      attempt.deliveryOutcome
                                                        .statusAfter
                                                    )}
                                                  </Text>
                                                  <Text size="xs">
                                                    Report version:{" "}
                                                    {attempt.reportVersion ??
                                                      item.reportVersion ??
                                                      "Not available"}
                                                  </Text>
                                                  <Text size="xs">
                                                    Overall attempt:{" "}
                                                    {formatRecallAttemptOutcome(
                                                      attempt.attemptOutcome
                                                    )}
                                                  </Text>
                                                  <Text size="xs">
                                                    Recalled by:{" "}
                                                    {attempt.actorName}
                                                  </Text>
                                                  <Text size="xs">
                                                    Authority:{" "}
                                                    {getRecallAuthorityLabel(
                                                      attempt.authorityBasis
                                                    )}
                                                  </Text>
                                                  <Text size="xs">
                                                    Time:{" "}
                                                    {dayjs(
                                                      attempt.attemptedAt
                                                    ).format(
                                                      "MMM D, YYYY h:mm A"
                                                    )}
                                                  </Text>
                                                  <Text size="xs">
                                                    Reason:{" "}
                                                    {attempt.reason ||
                                                      "No reason recorded"}
                                                  </Text>
                                                </Stack>
                                              )
                                            )}
                                          </Stack>
                                        </Popover.Dropdown>
                                      </Popover>
                                    )}
                                    {item.status === "recalled" &&
                                      !item.recallHistory?.length && (
                                        <Text size="xs" c="dimmed">
                                          Recall details unavailable
                                        </Text>
                                      )}
                                    {item.lastError && (
                                      <Text size="xs" c="dimmed" maw={240}>
                                        {item.lastError}
                                      </Text>
                                    )}
                                  </Stack>
                                </Table.Td>
                                <Table.Td>
                                  <Tooltip
                                    label={dayjs(item.scheduledAt).format(
                                      "MMM D, YYYY h:mm:ss A"
                                    )}
                                  >
                                    <Stack gap={0}>
                                      <Text size="sm">
                                        {dayjs(item.scheduledAt).format(
                                          "MMM D, h:mm A"
                                        )}
                                      </Text>
                                      {(item.status === "pending" ||
                                        item.status === "sending") && (
                                        <Text size="xs" c="dimmed">
                                          {getTimeUntil(item.scheduledAt)}
                                        </Text>
                                      )}
                                    </Stack>
                                  </Tooltip>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">
                                    {item.sentAt
                                      ? dayjs(item.sentAt).format("h:mm A")
                                      : "-"}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{item.deliveryAttempts}</Text>
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  )}
                </Stack>
              </Paper>
            </Tabs.Panel>

            {/* Delivered Tab */}
            <Tabs.Panel value="delivered" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Group gap="sm">
                      <ThemeIcon color="green" variant="light" size="sm">
                        <IconCheck size={14} />
                      </ThemeIcon>
                      <Text fw={500} size="lg">
                        Delivered ({deliveredTotal})
                      </Text>
                      <Button
                        leftSection={<IconRefresh size={16} />}
                        variant="light"
                        color="blue"
                        size="xs"
                        disabled={
                          selectedDeliveredItems.size === 0 || isPlaceholderData
                        }
                        loading={resendMutation.isPending}
                        onClick={handleBulkResend}
                      >
                        Resend ({selectedDeliveredItems.size})
                      </Button>
                    </Group>
                    {deliveredTotalPages > 1 && (
                      <Group gap="md">
                        <Text size="sm" c="dimmed">
                          Showing{" "}
                          {(deliveredPageNumber - 1) * ITEMS_PER_PAGE + 1}-
                          {Math.min(
                            deliveredPageNumber * ITEMS_PER_PAGE,
                            deliveredTotal
                          )}{" "}
                          of {deliveredTotal}
                        </Text>
                        <Pagination
                          total={deliveredTotalPages}
                          value={deliveredPageNumber}
                          onChange={handleDeliveredPageChange}
                          size="sm"
                        />
                      </Group>
                    )}
                  </Group>

                  {isError ? (
                    <Alert
                      color="red"
                      variant="light"
                      title="Could not load delivered reports"
                      role="alert"
                    >
                      Please refresh the page and try again.
                    </Alert>
                  ) : isLoading || isPlaceholderData ? (
                    <Group justify="center" p="xl">
                      <Loader />
                    </Group>
                  ) : deliveredItems.length === 0 ? (
                    <Text c="dimmed" ta="center" py="xl">
                      No delivered items
                    </Text>
                  ) : (
                    <Table.ScrollContainer minWidth={1280} type="native">
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th w={40}>
                              <Checkbox
                                checked={allDeliveredSelected}
                                indeterminate={
                                  currentDeliveredPageSelectedCount > 0 &&
                                  !allDeliveredSelected
                                }
                                onChange={handleSelectAllDeliveredChange}
                              />
                            </Table.Th>
                            <Table.Th>Case</Table.Th>
                            <Table.Th>Recipient</Table.Th>
                            <Table.Th>Method</Table.Th>
                            <Table.Th>Destination</Table.Th>
                            <Table.Th>Scheduled</Table.Th>
                            <Table.Th>Sent At</Table.Th>
                            <Table.Th>Delivered At</Table.Th>
                            <Table.Th>Attempts</Table.Th>
                            <Table.Th>Access</Table.Th>
                            <Table.Th w={48} />
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {deliveredItems.map((item) => {
                            const MethodIcon = getMethodIcon(
                              item.deliveryMethod
                            );
                            const isPortal = item.deliveryMethod
                              ?.toLowerCase()
                              .includes("portal");
                            return (
                              <Table.Tr key={item.id}>
                                <Table.Td>
                                  <Checkbox
                                    checked={selectedDeliveredItems.has(
                                      item.id
                                    )}
                                    onChange={handleSelectDeliveredItemChange}
                                    data-delivery-id={item.id}
                                  />
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" fw={500}>
                                    {item.caseNumber}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Stack gap={0}>
                                    <Text size="sm">{item.recipientName}</Text>
                                    <Text size="xs" c="dimmed">
                                      {getRecipientTypeLabel(
                                        item.recipientType
                                      )}
                                    </Text>
                                  </Stack>
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs">
                                    <ThemeIcon
                                      color="gray"
                                      variant="light"
                                      size="sm"
                                    >
                                      <MethodIcon size={14} />
                                    </ThemeIcon>
                                    <Text size="sm">
                                      {formatDeliveryMethod(
                                        item.deliveryMethod
                                      )}
                                    </Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td>
                                  <Text
                                    size="sm"
                                    style={{ maxWidth: 150 }}
                                    truncate
                                  >
                                    {item.destination}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">
                                    {dayjs(item.scheduledAt).format(
                                      "MMM D, h:mm A"
                                    )}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Stack gap={4} align="flex-start">
                                    <Text size="sm">
                                      {item.sentAt
                                        ? dayjs(item.sentAt).format(
                                            "MMM D, h:mm A"
                                          )
                                        : "-"}
                                    </Text>
                                    {item.digestState === "pending" && (
                                      <PortalDigestEmailTimingBadge
                                        reportAvailable={isReportOnPortal(
                                          item.status
                                        )}
                                      />
                                    )}
                                    {item.heldEmailId && (
                                      <HeldForReviewDeliveryBadge
                                        heldEmailId={item.heldEmailId}
                                      />
                                    )}
                                  </Stack>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">
                                    {item.deliveredAt
                                      ? dayjs(item.deliveredAt).format(
                                          "MMM D, h:mm A"
                                        )
                                      : "-"}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{item.deliveryAttempts}</Text>
                                </Table.Td>
                                <Table.Td>
                                  {isPortal ? (
                                    <Badge
                                      variant="light"
                                      color={
                                        revokedPortalMap[item.id]
                                          ? "gray"
                                          : "green"
                                      }
                                    >
                                      {revokedPortalMap[item.id]
                                        ? "Revoked"
                                        : "Active"}
                                    </Badge>
                                  ) : null}
                                </Table.Td>
                                <Table.Td>
                                  {isPortal ? (
                                    <Menu
                                      position="bottom-end"
                                      offset={4}
                                      withinPortal
                                      shadow="md"
                                    >
                                      <Menu.Target>
                                        <ActionIcon
                                          variant="subtle"
                                          color="gray"
                                          aria-label="Portal access actions"
                                          loading={
                                            portalActionItemId === item.id
                                          }
                                        >
                                          <IconDotsVertical size={18} />
                                        </ActionIcon>
                                      </Menu.Target>
                                      <Menu.Dropdown>
                                        {revokedPortalMap[item.id] ? (
                                          <Menu.Item
                                            leftSection={
                                              <IconCheck size={16} />
                                            }
                                            color="green"
                                            onClick={handleRestorePortalRequest}
                                            data-delivery-id={item.id}
                                          >
                                            Restore access
                                          </Menu.Item>
                                        ) : (
                                          <Menu.Item
                                            leftSection={<IconBan size={16} />}
                                            color="red"
                                            onClick={handleRevokePortalRequest}
                                            data-delivery-id={item.id}
                                          >
                                            Revoke access
                                          </Menu.Item>
                                        )}
                                      </Menu.Dropdown>
                                    </Menu>
                                  ) : null}
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  )}
                </Stack>
              </Paper>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Box>

      {/* Filter Modal */}
      <Modal
        opened={filterModalOpened}
        onClose={closeFilterModal}
        title="Filter Delivery Queue"
        size="md"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Search"
            placeholder="Search case, recipient..."
            leftSection={<IconSearch size={16} />}
            value={tempSearchValue}
            onChange={handleTempSearchChange}
          />
          <Select
            label="Status"
            placeholder="All Statuses"
            data={STATUS_OPTIONS}
            value={tempStatusFilter}
            onChange={handleTempStatusChange}
            clearable
          />
          <Select
            label="Method"
            placeholder="All Methods"
            data={METHOD_OPTIONS}
            value={tempMethodFilter}
            onChange={handleTempMethodChange}
            clearable
          />
          <Checkbox
            label="Show Failed Only"
            checked={tempShowFailedOnly}
            onChange={handleTempFailedOnlyChange}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClearTempFilters}>
              Clear All
            </Button>
            <Button variant="default" onClick={closeFilterModal}>
              Cancel
            </Button>
            <Button onClick={handleApplyFilters}>Apply Filters</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Portal Access Confirmation Modal */}
      <Modal
        opened={!!portalConfirm}
        onClose={handleClosePortalConfirmation}
        title={
          portalConfirm?.action === "revoke"
            ? "Revoke Portal Access"
            : "Restore Portal Access"
        }
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            {portalConfirm?.action === "revoke"
              ? "Are you sure you want to revoke portal access for this delivery? The recipient will no longer be able to view the report via the portal link."
              : "Are you sure you want to restore portal access for this delivery? The recipient will be able to view the report again via the portal link."}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClosePortalConfirmation}>
              Cancel
            </Button>
            <Button
              color={portalConfirm?.action === "revoke" ? "red" : "green"}
              loading={!!portalActionItemId}
              onClick={handleConfirmPortalAction}
            >
              {portalConfirm?.action === "revoke"
                ? "Revoke Access"
                : "Allow Access"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <CreateDeliveryModal
        opened={createDeliveryOpened}
        onClose={closeCreateDelivery}
        onSuccess={handleCreateDeliverySuccess}
      />

      <BulkDeliveryModal
        opened={bulkDeliveryOpened}
        onClose={closeBulkDelivery}
        onSuccess={handleCreateDeliverySuccess}
      />

      {manageHeldMethodsOpened && (
        <ManageSuppressedMethodsModal
          onClose={closeManageHeldMethods}
          availableMethods={availableMethods}
          suppressedMethods={blockedMethods}
        />
      )}
    </Container>
  );
}
